import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  UsersThree, UserPlus, UsersFour, CalendarStar, Buildings,
  ArrowRight, PencilSimple, GraduationCap, TrendUp,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import InviteDialog from "@/components/InviteDialog";
import OnboardingChecklist from "@/components/OnboardingChecklist";

function StatCard({ icon: Icon, label, value, to }) {
  return (
    <Link to={to}>
      <Card className="group flex items-center gap-4 border-border/60 p-5 shadow-sm transition-colors hover:border-secondary/40" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground"><Icon size={22} weight="duotone" /></span>
        <div>
          <div className="font-heading text-2xl font-semibold leading-none">{value}</div>
          <div className="mt-1 text-xs text-muted-foreground">{label}</div>
        </div>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [events, setEvents] = useState([]);
  const [myInsts, setMyInsts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/network/people-you-may-know").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/events/mine").then((r) => setEvents((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/institutions/mine").then((r) => setMyInsts(r.data || [])).catch(() => {});
    // Carry over a sandbox draft created before signup → publish it as a real site.
    let draft = null;
    try { draft = localStorage.getItem("sandbox_draft"); } catch { draft = null; }
    if (draft) {
      try { localStorage.removeItem("sandbox_draft"); } catch { /* ignore */ }
      api.post("/sandbox/publish", JSON.parse(draft))
        .then((r) => { toast.success("Your website from the sandbox is now live!"); nav(r.data.builder_path); })
        .catch(() => {});
    }
  }, [nav]);

  const connect = async (id) => {
    await api.post("/connections", { addressee_id: id });
    setSuggestions((s) => s.filter((x) => x.user_id !== id));
  };

  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-secondary mb-1">Welcome back</p>
          <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">{user?.name?.split(" ")[0]}, your community awaits.</h1>
        </div>
        <Link to="/profile/edit"><Button variant="outline" className="gap-2 rounded-full"><PencilSimple size={16} /> Edit profile</Button></Link>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={UsersThree} label="Connections" value={stats?.connections ?? "—"} to="/connections" />
        <StatCard icon={UsersFour} label="Communities" value={stats?.groups ?? "—"} to="/groups" />
        <StatCard icon={CalendarStar} label="Events" value={stats?.events ?? "—"} to="/events" />
        <StatCard icon={Buildings} label="Institutions" value={stats?.institutions_total ?? "—"} to="/institutions" />
      </div>

      {myInsts.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-secondary/25 bg-accent/40 p-5" data-testid="my-org-card">
          <div className="flex items-center gap-3">
            <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-secondary text-secondary-foreground"><Buildings size={22} weight="duotone" /></span>
            <div>
              <p className="overline text-secondary">Your institution / org page</p>
              <h2 className="font-heading text-lg font-medium">{myInsts[0].name}</h2>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/institutions/${myInsts[0].id}`}><Button variant="outline" className="rounded-full gap-1.5" data-testid="manage-org-btn"><UsersFour size={16} /> Manage team &amp; page</Button></Link>
            <Link to={`/institutions/${myInsts[0].id}/build`}><Button className="rounded-full gap-1.5"><PencilSimple size={16} /> Website builder</Button></Link>
          </div>
        </div>
      )}

      <div className="mt-8"><OnboardingChecklist stats={stats} /></div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Completion */}
          <Card className="border-border/60 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendUp className="text-secondary" size={20} weight="duotone" /><h2 className="font-heading text-lg font-medium">Profile completion</h2></div>
              <span className="font-heading text-2xl font-semibold text-secondary">{stats?.completion ?? 0}%</span>
            </div>
            <Progress value={stats?.completion ?? 0} className="mt-4 h-2" />
            <p className="mt-3 text-sm text-muted-foreground">A complete profile helps classmates find and recognise you. Add photos, education and skills.</p>
            <Link to="/profile/edit"><Button size="sm" className="mt-4 rounded-full gap-1.5" data-testid="dashboard-complete-profile">Complete now <ArrowRight size={15} /></Button></Link>
          </Card>

          {/* Upcoming events */}
          <Card className="border-border/60 p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-medium">Upcoming events</h2>
              <Link to="/events" className="text-sm text-secondary hover:underline">View all</Link>
            </div>
            <div className="space-y-3">
              {events.length === 0 && <p className="text-sm text-muted-foreground py-4">No events yet. Host a reunion!</p>}
              {events.map((e) => (
                <Link key={e.id} to="/events" className="flex items-center gap-4 rounded-xl border border-border/60 p-3 transition-colors hover:bg-accent/50">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
                    <div className="text-center leading-none">
                      <div className="text-[10px]">{new Date(e.date).toLocaleString("en", { month: "short" })}</div>
                      <div className="font-heading text-lg font-semibold">{new Date(e.date).getDate()}</div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{e.title}</div>
                    <div className="truncate text-xs text-muted-foreground">{e.location || e.venue || "Virtual"} · {e.rsvp_count ?? (e.rsvps?.length || 0)} going</div>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* People you may know */}
        <Card className="h-fit border-border/60 p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2"><GraduationCap className="text-secondary" size={20} weight="duotone" /><h2 className="font-heading text-lg font-medium">People you may know</h2></div>
          <div className="space-y-4">
            {suggestions.length === 0 && <p className="text-sm text-muted-foreground py-4">Add your education to discover classmates.</p>}
            {suggestions.slice(0, 6).map((p) => {
              const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
              const shared = (p.shared || [])[0];
              return (
                <div key={p.user_id} className="flex items-start gap-3" data-testid="suggestion-item">
                  <Avatar className="h-10 w-10"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent text-xs">{initials}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <Link to={`/u/${p.public_slug || p.user_id}`} className="block truncate text-sm font-medium hover:underline">{p.name}</Link>
                    <div className="truncate text-xs text-muted-foreground">{p.headline || "Alumnus"}</div>
                    {shared && <div className="mt-0.5 truncate text-[11px] text-secondary">via {shared.label}{p.mutual > 1 ? ` +${p.mutual - 1} more` : ""}</div>}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Button size="sm" variant="secondary" className="h-7 rounded-full px-3 text-xs gap-1" onClick={() => connect(p.user_id)} data-testid="suggestion-connect"><UserPlus size={13} /> Connect</Button>
                      {shared?.id && <InviteDialog type={shared.type} id={shared.id} label="Invite" size="sm" variant="ghost" className="h-7 px-2 text-xs" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </main>
  );
}
